// hooks/useFetchData.ts
import { useState } from "react";
import { getDataTableData, getDataTableMeta } from "../../../../api/toga";
import { TableFieldValues } from "./useDataTableState";
import { useSearchParams } from "react-router-dom";
type StatusValue = { slug: string; name: string; colorHex: string };

type UseFetchDataOptions = {
	slug: string;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setColumnMappings?: React.Dispatch<React.SetStateAction<any>>;
	setData: React.Dispatch<React.SetStateAction<any[]>>;
	setColumnTitles: React.Dispatch<React.SetStateAction<string[]>>;
	setTotalPages: React.Dispatch<React.SetStateAction<number>>;
	setTotalRecords: React.Dispatch<React.SetStateAction<number>>;
	setIsError: React.Dispatch<React.SetStateAction<boolean>>;
	setNoDataFound: React.Dispatch<React.SetStateAction<boolean>>;
	nestedPrimaryRecordSlug?: string | null;
	nestedTableUuid?: string | null;
	setChildColumnTitles?: React.Dispatch<React.SetStateAction<string[]>>;
	setChildColumnMappings?: React.Dispatch<React.SetStateAction<any>>;
	setTotalPagesInChildTable?: React.Dispatch<React.SetStateAction<number>>;
	setDataTableValueFields?: React.Dispatch<
		React.SetStateAction<TableFieldValues[]>
	>;
	setTypeValues: React.Dispatch<React.SetStateAction<StatusValue[]>>;
	additionalData?: any;
	pageTitle: string;
	selectedPurchaseOrderItemUuid?: string;
	subtableSlug?: string;
	subtableUuid?: string;
};

type FetchArgs = {
	page?: number;
	append?: boolean;
};

const useFetchData = ({
	slug,
	setIsLoading,
	setColumnMappings,
	setData,
	setColumnTitles,
	setTotalPages,
	setTotalRecords,
	setIsError,
	setNoDataFound,
	setChildColumnTitles,
	nestedPrimaryRecordSlug = null,
	nestedTableUuid = null,
	setTotalPagesInChildTable,
	setDataTableValueFields,
	setTypeValues,
	additionalData,
	selectedPurchaseOrderItemUuid,
	subtableSlug,
	subtableUuid,
}: UseFetchDataOptions) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [searchParams] = useSearchParams();
	const selectedGrouping = JSON.parse(
		localStorage.getItem("selectedGrouping") ?? "[]"
	);

	const fetchData = async ({ page, append = false }: FetchArgs = {}) => {
		const pageToFetch =
			page !== undefined ? page : append ? currentPage + 1 : 1;

		setCurrentPage(pageToFetch);
		if (!append) setIsLoading(true);

		try {
			const hasUnitsSubtableParentItemParam = searchParams.has(
				"inventory_items:sales-orders-item-uuid"
			);

			const unitsSubtableParentItemUuid = searchParams.get(
				"inventory_items:sales-orders-item-uuid"
			);
			// THIS IS RETURNING THE SELECTED PURCHASE ORDER ITEM IN THE SUBTABLE
			if (
				hasUnitsSubtableParentItemParam &&
				unitsSubtableParentItemUuid
			) {
				slug = "items-for-purchase-orders";
				nestedTableUuid = unitsSubtableParentItemUuid;
				nestedPrimaryRecordSlug = "SalesOrderItems";
			}

			if (
				hasUnitsSubtableParentItemParam &&
				unitsSubtableParentItemUuid &&
				subtableSlug &&
				subtableUuid
			) {
				slug = "units-for-items-for-purchase-orders";
			}

			const tableMeta = await getDataTableMeta(slug);
			if (!tableMeta || !tableMeta.table) {
				console.error("No table metadata found");
				setIsError(true);
				return;
			}

			const { table, nestedTable } = tableMeta;
			const fields = table.fields ?? [];

			const statusField = fields.find(
				(f) => f.settings.field === "_status"
			) as any;
			const statusValues = statusField?.values as
				| StatusValue[]
				| undefined;
			if (statusValues) {
				setTypeValues(statusValues);
			}

			if (
				table.slug === "purchase-orders" &&
				nestedPrimaryRecordSlug !== null
			) {
				nestedTableUuid = "";
				nestedPrimaryRecordSlug = "";
			}

			const tableData = await getDataTableData(
				tableMeta as any,
				nestedPrimaryRecordSlug,
				nestedTableUuid,
				{ ...(additionalData ?? {}), page: pageToFetch },
				selectedPurchaseOrderItemUuid,
				subtableSlug,
				subtableUuid
			);

			if (!tableData) {
				console.error("No table data found");
				setIsError(true);
				return;
			}

			const rows = tableData.rows ?? tableData;

			setData((prev) =>
				append ? [...(prev as any[]), ...(rows as any[])] : rows
			);
			setColumnMappings?.(
				table.fields
					.filter(
						(f) => f.isVisible !== false && f.isVisible !== null
					)
					.map((f) => ({
						name: f.settings.table,
						slug: f.slug,
						settings: f.settings,
						isCopyable: f.isCopyable,
						isVisible: f.isVisible,
					}))
			);
			setDataTableValueFields?.(table.fields as TableFieldValues[]);
			setColumnTitles(table.fields.map((f) => f.settings.field));
			setTotalPages(tableData.meta.totalPageCount ?? 0);
			setTotalRecords(tableData.meta.totalRecordCount ?? 0);
			setNoDataFound((rows as any[]).length === 0);

			setIsError(false);
		} catch (err) {
			console.error(err);
			setIsError(true);
		} finally {
			if (!append) setIsLoading(false);
		}
	};

	return fetchData;
};

export default useFetchData;

// // hooks/useDataTable.ts
// import { useQuery } from "@tanstack/react-query";
// import { useSearchParams } from "react-router-dom";
// import { getDataTableData, getDataTableMeta } from "../../../../api/toga";
// import { TableFieldValues } from "./useDataTableState";

// type StatusValue = { slug: string; name: string; colorHex: string };

// type UseDataTableOptions = {
// 	slug: string;
// 	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
// 	setColumnMappings?: React.Dispatch<React.SetStateAction<any>>;
// 	page?: number;
// 	nestedPrimaryRecordSlug?: string | null;
// 	nestedTableUuid?: string | null;
// 	selectedPurchaseOrderItemUuid?: string;
// 	subtableSlug?: string;
// 	subtableUuid?: string;
// 	additionalData?: any;
// 	enabled?: boolean;
// };

// const useDataTable = ({
// 	slug: initialSlug,
// 	setIsLoading,
// 	setColumnMappings,
// 	setData,
// 	setColumnTitles,
// 	setTotalPages,
// 	setIsError,
// 	setNoDataFound,
// 	nestedPrimaryRecordSlug,
// 	nestedTableUuid,
// 	setDataTableValueFields,
// 	setTypeValues,
// 	additionalData,
// 	setTotalRecords,
// 	pageTitle,
// 	selectedPurchaseOrderItemUuid,
// 	subtableSlug,
// 	subtableUuid,

// 	page = 1,
// 	nestedPrimaryRecordSlug: initialNestedPrimaryRecordSlug = null,
// 	enabled = true,
// }: UseDataTableOptions) => {
// 	const [searchParams] = useSearchParams();

// 	// Determine actual slug and nested params based on search params
// 	const hasUnitsSubtableParentItemParam = searchParams.has(
// 		"inventory_items:sales-orders-item-uuid",
// 	);
// 	const unitsSubtableParentItemUuid = searchParams.get(
// 		"inventory_items:sales-orders-item-uuid",
// 	);

// 	let slug = initialSlug;
// 	let nestedTableUuid = initialNestedTableUuid;
// 	let nestedPrimaryRecordSlug = initialNestedPrimaryRecordSlug;

// 	if (hasUnitsSubtableParentItemParam && unitsSubtableParentItemUuid) {
// 		slug = "items-for-purchase-orders";
// 		nestedTableUuid = unitsSubtableParentItemUuid;
// 		nestedPrimaryRecordSlug = "SalesOrderItems";
// 	}

// 	if (
// 		hasUnitsSubtableParentItemParam &&
// 		unitsSubtableParentItemUuid &&
// 		subtableSlug &&
// 		subtableUuid
// 	) {
// 		slug = "units-for-items-for-purchase-orders";
// 	}

// 	if (slug === "purchase-orders" && initialNestedPrimaryRecordSlug !== null) {
// 		nestedTableUuid = "";
// 		nestedPrimaryRecordSlug = "";
// 	}

// 	return useQuery({
// 		queryKey: [
// 			"dataTable",
// 			slug,
// 			page,
// 			nestedPrimaryRecordSlug,
// 			nestedTableUuid,
// 			selectedPurchaseOrderItemUuid,
// 			subtableSlug,
// 			subtableUuid,
// 			additionalData,
// 		],
// 		queryFn: async () => {
// 			// Fetch metadata
// 			const tableMeta = await getDataTableMeta(slug);
// 			if (!tableMeta || !tableMeta.table) {
// 				throw new Error("No table metadata found");
// 			}

// 			const { table } = tableMeta;
// 			const fields = table.fields ?? [];

// 			// Extract status values
// 			const statusField = fields.find(
// 				(f) => f.settings.field === "_status",
// 			) as any;
// 			const statusValues = (statusField?.values ?? []) as StatusValue[];

// 			// Fetch table data
// 			const tableData = await getDataTableData(
// 				tableMeta as any,
// 				nestedPrimaryRecordSlug,
// 				nestedTableUuid,
// 				{ ...(additionalData ?? {}), page },
// 				selectedPurchaseOrderItemUuid,
// 				subtableSlug,
// 				subtableUuid,
// 			);

// 			if (!tableData) {
// 				throw new Error("No table data found");
// 			}

// 			const rows = tableData.rows ?? tableData;

// 			return {
// 				rows,
// 				columnMappings: table.fields
// 					.filter(
// 						(f) => f.isVisible !== false && f.isVisible !== null,
// 					)
// 					.map((f) => ({
// 						name: f.settings.table,
// 						slug: f.slug,
// 						settings: f.settings,
// 						isCopyable: f.isCopyable,
// 						isVisible: f.isVisible,
// 					})),
// 				columnTitles: table.fields.map((f) => f.settings.field),
// 				dataTableValueFields: table.fields as TableFieldValues[],
// 				statusValues,
// 				totalPages: tableData.meta.totalPageCount ?? 0,
// 				totalRecords: tableData.meta.totalRecordCount ?? 0,
// 				noDataFound: (rows as any[]).length === 0,
// 			};
// 		},
// 		enabled,
// 		staleTime: 30000, // Adjust as needed
// 		// Keep previous data while fetching new page
// 		placeholderData: (previousData) => previousData,
// 	});
// };

// export default useDataTable;
